import React, { useState } from 'react';
import { Button, message, Modal, Upload } from 'antd';
import { CameraOutlined, CompressOutlined, UploadOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { slideAction } from '../../store/slideSlice';
import { templateService } from '../../store/api';
import domtoimage from 'dom-to-image';
import { uploadToCloudinary } from '../../utils/cloudinary';
import defaultTemplateImage from '../../assets/template-default-image.svg';
import './TemplateThumbnail.css';

// Compress an image File to a Blob using Canvas.
// Resizes so the longest side is at most `maxSide` px and encodes as JPEG at `quality` (0–1).
const compressImage = (file, { maxSide = 1280, quality = 0.8 } = {}) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      let { width, height } = img;
      if (width > maxSide || height > maxSide) {
        if (width >= height) {
          height = Math.round((height * maxSide) / width);
          width = maxSide;
        } else {
          width = Math.round((width * maxSide) / height);
          height = maxSide;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Canvas compression failed'))),
        'image/jpeg',
        quality,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('Failed to load image')); };
    img.src = objectUrl;
  });

// Threshold above which we ask the user about compression (500 KB)
const COMPRESS_THRESHOLD_BYTES = 500 * 1024;

const formatFileSize = (bytes) => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  return `${(bytes / 1024).toFixed(1)} KB`;
};

const askToCompress = (file) =>
  new Promise((resolve) => {
    Modal.confirm({
      title: 'Large image detected',
      icon: <CompressOutlined />,
      content: (
        <span>
          Your image is <strong>{formatFileSize(file.size)}</strong>. Would you like to
          compress it before uploading for faster performance?
        </span>
      ),
      okText: 'Compress & Upload',
      cancelText: 'Upload Original',
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });

const TemplateThumbnail = ({ viewMode, onMenuClick }) => {

  const dispatch = useDispatch();
  const { id } = useParams();
  const isTemplateDocument = useSelector((state) => state.slide.isTemplateDocument);
  const templateData = useSelector((state) => state.slide.templateData);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [customUrl, setCustomUrl] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const existingThumbnail = templateData?.templateThumbnail || defaultTemplateImage;

  const handleOpenModal = () => {
    setPreviewUrl(null);
    setIsModalVisible(true);
  };

  const handleCapture = async () => {
    try {
      // Check if slide element exists
      const slideElement = document.getElementById('slide01');
      if (!slideElement) {
        message.error({ content: 'No slide available to capture. Please open a slide first.', key: 'capture' });
        return;
      }
      setIsCapturing(true);
      message.loading({ content: 'Capturing and uploading slide...', key: 'capture' });
      const dataUrl = await domtoimage.toPng(document.getElementById('slide01'));
      const cloudinaryUrl = await uploadToCloudinary(dataUrl);
      setPreviewUrl(cloudinaryUrl);
      message.success({ content: 'Slide captured successfully!', key: 'capture' });
    } catch (error) {
      console.error('Failed to capture slide:', error);
      message.error({ content: 'Failed to capture slide', key: 'capture' });
    } finally {
      setIsCapturing(false);
    }
  };

  const handleConfirmUpdate = async () => {
    try {
      const thumbnailUrl = customUrl || previewUrl;

      if (!thumbnailUrl) {
        message.warning('Please capture or upload a thumbnail first');
        return;
      }

      const res = await templateService.updateTemplate({ templateId: id, templateThumbnail: thumbnailUrl });
      message.success('Thumbnail saved successfully');
      setIsModalVisible(false);
      setPreviewUrl(null);
      setCustomUrl(null);
      // Update templateData in store
      if (res?.data) {
        dispatch(slideAction.setTemplateData({ ...templateData, ...res.data, templateThumbnail: thumbnailUrl }));
      } else {
        dispatch(slideAction.setTemplateData({ ...templateData, templateThumbnail: thumbnailUrl }));
      }
    } catch (error) {
      console.error('Failed to save thumbnail:', error);
      message.error('Failed to save thumbnail');
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setPreviewUrl(null);
    setCustomUrl(null);
  };

  const handleUpload = async (file) => {
    try {
      setIsUploading(true);
      let fileToUpload = file;
      if (file.size > COMPRESS_THRESHOLD_BYTES) {
        const shouldCompress = await askToCompress(file);
        if (shouldCompress) {
          message.loading({ content: 'Compressing and uploading image...', key: 'upload' });
          fileToUpload = await compressImage(file);
        } else {
          message.loading({ content: 'Uploading original image...', key: 'upload' });
        }
      } else {
        message.loading({ content: 'Uploading image...', key: 'upload' });
      }
      const cloudinaryUrl = await uploadToCloudinary(fileToUpload);
      setCustomUrl(cloudinaryUrl);
      message.success({ content: 'Image uploaded successfully!', key: 'upload' });
    } catch (error) {
      console.error('Failed to upload image:', error);
      message.error({ content: 'Failed to upload image', key: 'upload' });
    } finally {
      setIsUploading(false);
    }
    return false;
  };

  const modalContent = (
    <Modal
      title={<div className="thumbnail-modal-header"><CameraOutlined className="thumbnail-modal-icon" />Thumbnail Preview</div>}
      visible={isModalVisible}
      onCancel={handleCancel}
      className="thumbnail-modal"
      width={560}
      footer={[
        <Button key="cancel" onClick={handleCancel} className="cancel-btn">
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleConfirmUpdate} className="update-thumbnail-btn">
          Save Thumbnail
        </Button>,
      ]}
    >
      <div className="thumbnail-content">
        <div className="thumbnail-preview-container" role="img" aria-label="thumbnail-preview">
          {(customUrl || previewUrl || existingThumbnail) ? (
            <img src={customUrl || previewUrl || existingThumbnail} alt="Thumbnail Preview" className="thumbnail-preview" />
          ) : (
            <div className="thumbnail-placeholder">
              <div className="placeholder-icon">📄</div>
              <p>No thumbnail yet</p>
            </div>
          )}
        </div>
        <div className="upload-section">
          <div className="upload-label">Upload a custom thumbnail</div>
          <Upload
            beforeUpload={handleUpload}
            showUploadList={false}
            accept="image/*"
            disabled={isUploading}
          >
            <Button icon={<UploadOutlined />} className="upload-btn" loading={isUploading} disabled={isUploading}>
              {isUploading ? 'Uploading...' : 'Upload Custom Thumbnail'}
            </Button>
          </Upload>
          <div className="capture-label">Capture current view</div>
          <Button 
            icon={<CameraOutlined />} 
            onClick={handleCapture} 
            className="capture-btn"
            loading={isCapturing}
            disabled={isCapturing}
          >
            {isCapturing ? 'Capturing...' : 'Capture Current View'}
          </Button>
        </div>
      </div>
    </Modal>
  );

  if (!isTemplateDocument) return null;

  if (viewMode === "menu") {
    return (
      <>
        <div className='menu-item' onClick={() => { handleOpenModal(); onMenuClick && onMenuClick(); }}>
          <CameraOutlined className="toggle-menu-icon" />
          <span>Update Thumbnail</span>
        </div>
        {modalContent}
      </>
    );
  }

  return (
    <>
      <Button
        type="text"
        icon={<CameraOutlined className="thumbnail-button-icon" />}
        onClick={handleOpenModal}
        title="Save Thumbnail"
        size="small"
        className="thumbnail-button"
      />
      {modalContent}
    </>
  );
};

export default TemplateThumbnail;